import { insetBorder } from './insetBorder'
import type { Components } from '@mui/material'

export const OmuiNumberStepper: NonNullable<Components['OmuiNumberStepper']> = {
  styleOverrides: {
    root: ({ theme, ownerState }) => ({
      // Allow position: absolute ::after inside
      isolation: 'isolate',
      position: 'relative',
      display: 'inline-flex',
      borderRadius: theme.shape['futureTheme.radius-lg'],
      // Shadow is on an ::after to achieve the "inset" effect while still allowing for a background color on the nested input
      '::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: theme.shape['futureTheme.radius-lg'],
        pointerEvents: 'none',
        boxShadow: insetBorder({
          width: theme.futureTheme.strokeWidth.default,
          color: theme.palette.futureTheme.border.strong,
        }),
        zIndex: 2,
      },
      // Stop us interfering with the input's focus box-shadow
      '&:hover::after': {
        zIndex: 0,
      },
      backgroundColor: theme.palette.futureTheme.bg.surface.default,

      ...(ownerState.error && {
        '&::after': {
          zIndex: 0,
        },
        '& .MuiInputBase-root + .MuiDivider-root': {
          zIndex: 0,
        },
        '& .MuiInputBase-root': {
          backgroundColor: theme.palette.futureTheme.bg.danger.muted,
          color: theme.palette.futureTheme.text.danger.onDangerMuted,
        },
        '& .MuiInputBase-root::after, & .MuiInputBase-root:hover:not(.Mui-disabled):not(.Mui-focused)::after': {
          boxShadow: insetBorder({
            width: theme.futureTheme.strokeWidth.default,
            color: theme.palette.futureTheme.border.danger.strong,
          }),
        },
        '& .MuiInputBase-root:not(.Mui-disabled).Mui-focused::after': {
          boxShadow: `${insetBorder({
            width: theme.futureTheme.strokeWidth.default,
            color: theme.palette.futureTheme.border.danger.strong,
          })}, ${theme.futureTheme.shadows.activeError}`,
        },
      }),
      ...(ownerState.size === 'small' && {
        '& .MuiInputBase-root': {
          padding: theme.spacing(1.5, 0, 1.5, 3),
        },
        '& .MuiIconButton-root': {
          margin: theme.spacing(1),
        },
      }),
      ...(ownerState.disabled && {
        backgroundColor: theme.palette.futureTheme.bg.surface.subtle,
      }),
    }),
    input: ({ theme }) => ({
      zIndex: 1,
      padding: theme.spacing(2, 0, 2, 3),
      ...theme.typography['futureTheme.body-regular'],
      color: theme.palette.futureTheme.text.default,
      borderTopLeftRadius: theme.shape['futureTheme.radius-lg'],
      borderBottomLeftRadius: theme.shape['futureTheme.radius-lg'],
      position: 'relative',
      '::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderTopLeftRadius: theme.shape['futureTheme.radius-lg'],
        borderBottomLeftRadius: theme.shape['futureTheme.radius-lg'],
        zIndex: 3,
      },
      '&:hover:not(.Mui-disabled):not(.Mui-focused)::after': {
        boxShadow: insetBorder({
          width: theme.futureTheme.strokeWidth.default,
          color: theme.palette.futureTheme.border.strongest,
        }),
      },
      '&.Mui-focused::after': {
        boxShadow: `${insetBorder({
          width: theme.futureTheme.strokeWidth.default,
          color: theme.palette.futureTheme.border.strongest,
        })}, ${theme.futureTheme.shadows.activeFocus}`,
      },
      '&.Mui-disabled': {
        color: theme.palette.futureTheme.text.default,
      },
      '& + .MuiDivider-root': {
        marginLeft: `-${theme.futureTheme.strokeWidth.default}`, // To put this under the boxShadow of the input
        // Required to be over the input error background
        zIndex: 1,
      },
      '&:hover + .MuiDivider-root': {
        // Be behind the input hover effect
        zIndex: 0,
      },
      '& .MuiInputBase-input': {
        padding: 0,
      },
    }),
    button: ({ theme }) => ({
      margin: theme.spacing(1.5),
      flexShrink: 0,
      alignSelf: 'center',
    }),
    divider: {},
  },
}
